import { useEffect, useState } from "react";
import { Snackbar, Alert } from "@mui/material";

// Minimal global toast, callable from anywhere (in particular the shared
// HTTP helpers in data/request.tsx) without needing every caller to thread
// error state through props. A plain module-level listener list rather than
// Context, since this has exactly one consumer (ToastHost, mounted once in
// App.tsx) and no component needs to read the current toast, only trigger one.
type Toast = { id: number; message: string; severity: "error" | "warning" | "info" };

let listeners: ((toast: Toast) => void)[] = [];
let nextId = 0;

export const showToast = (message: string, severity: Toast["severity"] = "error") => {
  const toast: Toast = { id: nextId++, message, severity };
  listeners.forEach((listener) => listener(toast));
};

export const ToastHost = () => {
  const [current, setCurrent] = useState<Toast | null>(null);

  useEffect(() => {
    const listener = (toast: Toast) => setCurrent(toast);
    listeners.push(listener);
    return () => {
      listeners = listeners.filter((l) => l !== listener);
    };
  }, []);

  return (
    <Snackbar
      open={current !== null}
      autoHideDuration={6000}
      onClose={() => setCurrent(null)}
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
    >
      {current ? (
        <Alert severity={current.severity} onClose={() => setCurrent(null)} variant="filled" sx={{ width: "100%" }}>
          {current.message}
        </Alert>
      ) : undefined}
    </Snackbar>
  );
};

export default ToastHost;
